import { useState } from 'react';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { IconChevronDown } from 'Ui/Icons';
import { formatPrice } from 'Utils/tools';
import { getBasket_basket } from '../HeaderBasket/__generated__/getBasket';

const TotalRow = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 1.4rem 1.6rem 0.8rem;
  background: none;
  border: none;
  cursor: pointer;
  width: 100%;
  text-align: left;
`;

const TotalLabel = styled.span`
  font-family: var(--font-primary);
  font-size: 1.6rem;
  font-weight: 700;
  color: #000;
  text-transform: uppercase;
  flex: 1;
`;

const TotalAmount = styled.span`
  font-family: var(--font-primary);
  font-size: 1.6rem;
  font-weight: 700;
  color: #000;
`;

const ChevronIcon = styled.span<{ $open: boolean }>`
  display: inline-flex;
  transition: transform 0.2s ease;
  transform: ${({ $open }) => ($open ? 'rotate(180deg)' : 'rotate(0deg)')};
  color: #000;
`;

const DetailsPanel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 1.6rem 1.2rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
`;

const DetailRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const DetailLabel = styled.span`
  font-family: var(--font-primary);
  font-size: 1.3rem;
  font-weight: 500;
  color: #000;
`;

const SavingsLabel = styled(DetailLabel)`
  color: #888;
`;

const SavingsAmount = styled.span`
  font-family: var(--font-primary);
  font-size: 1.3rem;
  font-weight: 500;
  color: #888;
  text-decoration: line-through;
`;

const DetailAmount = styled.span`
  font-family: var(--font-primary);
  font-size: 1.3rem;
  font-weight: 600;
  color: #000;
`;

const ShippingNote = styled.p`
  font-family: var(--font-primary);
  font-size: 1.2rem;
  font-weight: 400;
  color: #888;
  margin: 0;
`;

type Props = {
  basket: getBasket_basket;
};

export default function BasketBarOrderSummary({ basket }: Props) {
  const [showDetails, setShowDetails] = useState(false);
  const { t } = useTranslation('shipping', { useSuspense: false });

  const total = basket.totalPrice;
  const productCount = basket.productCount;
  const totalSaving = basket.totalSaving ?? 0;
  const promoCode = basket.basketPromoCode;
  const totalPlusShipping = basket.totalPricePlusShipping;
  const shippingAmount =
    totalPlusShipping != null ? totalPlusShipping - total : null;

  return (
    <>
      <TotalRow onClick={() => setShowDetails((v) => !v)}>
        <ChevronIcon $open={showDetails}>
          <IconChevronDown width={16} height={16} />
        </ChevronIcon>
        <TotalLabel>{t('totalTTC')}</TotalLabel>
        <TotalAmount>{formatPrice(total)}</TotalAmount>
      </TotalRow>

      {showDetails && (
        <DetailsPanel>
          <DetailRow>
            <DetailLabel>
              {t('subTotal')} / {productCount}{' '}
              {productCount === 1 ? t('articleSingular') : t('articlePlural')}
            </DetailLabel>
            <DetailAmount>{formatPrice(total)}</DetailAmount>
          </DetailRow>

          {totalSaving > 0 && (
            <DetailRow>
              <SavingsLabel>{t('economySaving')}</SavingsLabel>
              <SavingsAmount>{formatPrice(totalSaving)}</SavingsAmount>
            </DetailRow>
          )}

          {promoCode?.code && promoCode.value != null && (
            <DetailRow>
              <DetailLabel>
                {t('promoCode')} : {promoCode.code}
              </DetailLabel>
              <DetailAmount>-{formatPrice(promoCode.value)}</DetailAmount>
            </DetailRow>
          )}

          {shippingAmount != null && shippingAmount > 0 ? (
            <DetailRow>
              <DetailLabel>{t('shippingFees')}</DetailLabel>
              <DetailAmount>{formatPrice(shippingAmount)}</DetailAmount>
            </DetailRow>
          ) : (
            <ShippingNote>{t('shippingFeesNote')}</ShippingNote>
          )}
        </DetailsPanel>
      )}
    </>
  );
}
